import { createSlice } from "@reduxjs/toolkit";

import { clearAccessToken } from "components/utilities/sedra3/auth";

// The access token itself is not kept here: it lives in auth.js, in memory
// only (#112), and the state holds what the page shows of the session.
const initialState = {
  user: null,
  roles: [],
  permissions: [],
  isAuthenticated: false,
};

// What a sign-in leaves in localStorage for the next page load: the user's
// profile and the roles and permissions the access token named, never a token.
// A reload restores them and renews the access token (store.js, index.jsx).
const SESSION_RECORDS = ["user", "roles", "permissions"];

// Removed at sign-out with the session's records: what the permission library
// (react-permission-role) keeps of the user, the chosen language, the mark of
// the latest token handed between tabs (auth.js), and the tokens an earlier
// version of the portal kept here.
const OTHER_SESSION_KEYS = [
  "__permissionUser",
  "language",
  "sessionHandover",
  "accessToken",
  "refreshToken",
];

export function decodeJWT(token) {
  try {
    // Split the JWT into its parts
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

    // Decode Base64
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Invalid token format:", error);
    return null;
  }
}

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (state, action) => {
      const { user, accessToken } = action.payload;

      // Decode the token to extract roles and permissions
      const decodedToken = decodeJWT(accessToken);
      const { roles = [], permissions = [] } = decodedToken;

      state.user = user;
      state.roles = roles;
      state.permissions = permissions;
      state.isAuthenticated = true;

      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("roles", JSON.stringify(roles));
      localStorage.setItem("permissions", JSON.stringify(permissions));
    },
    // A page load in a signed-in browser: the session as the sign-in recorded
    // it, until the renewal says whether it goes on (index.jsx).
    sessionRestored: (state, action) => {
      const { user, roles = [], permissions = [] } = action.payload;
      state.user = user;
      state.roles = roles;
      state.permissions = permissions;
      state.isAuthenticated = true;
    },
    logout: (state) => {
      state.user = null;
      state.roles = [];
      state.permissions = [];
      state.isAuthenticated = false;

      // The access token this tab holds in memory. Its cookies are the
      // server's, HttpOnly: the logout's answer removes them.
      clearAccessToken();
      // Every record of the session in localStorage. react-permission-role
      // keeps the user's id, roles and permissions in `__permissionUser` and
      // offers no call that clears them.
      [...SESSION_RECORDS, ...OTHER_SESSION_KEYS].forEach((key) =>
        localStorage.removeItem(key)
      );
    },
  },
});

// The session a signed-in browser recorded (setCredentials), or null.
export const recordedSession = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) return null;
    return {
      user,
      roles: JSON.parse(localStorage.getItem("roles")) || [],
      permissions: JSON.parse(localStorage.getItem("permissions")) || [],
    };
  } catch {
    return null;
  }
};

export const { setCredentials, sessionRestored, logout } = authSlice.actions;
export default authSlice.reducer;

// Selectors
export const selectRoles = (state) => state.auth.roles;
export const selectPermissions = (state) => state.auth.permissions;

// Signed in and not signed out since. Whether the session is still good is the
// server's to say on the next request: a missing or expired access token is
// renewed there, and a session the server refuses signs the user out, which
// clears this.
export const selectHasSession = (state) => Boolean(state.auth.isAuthenticated);
